import type { InferType } from 'yup'

import { LOTTERY_ADD_ONS, type LotteryId as LotteryEnumsId } from '@lottocom/lottery-enums'
import { calculatePlaySlipPrices } from '@lottocom/price-calculation'

import {
  type AddOnInput,
  type FieldInput,
  SubscriptionType,
} from '#api/shared/@types/generated/backend/graphql-schema-types'
import { QUERY_ADDON_PREFIX, QUERY_SUBSCRIPTION } from '~/constants/queryParams'

import type { playSlipPlaySchema } from '../stores/schemas/playSlip'

import {
  type LotterySelectedAddOn,
  type LotterySelectedPerPlayAddOn,
  LotteryTicketType,
  type QuickPickSettings,
  updateNumberLotteryPlay,
  useLotteryStore,
} from '../stores/lottery'
import { useProductStore } from '../stores/product'
import {
  getInitialPerPlayAddOns,
  isFixedCharge,
  isNotPerPlay,
  isPerPlay,
  type LotteryConfig,
} from '../utils/lotteryCache'

export type PlaySlipPlay = InferType<typeof playSlipPlaySchema>

export type QuickPickOptions = Partial<Omit<QuickPickSettings, 'quantity'>> & {
  stake?: Maybe<string>
  validateFn?: (numbers: number[][]) => boolean
}

type BackendLotteryError = 'lottery.notfound' | (string & {})

type QuickPickPlay = {
  isQuickPick: boolean
  numbers: number[][]
  perPlayAddOn?: AddOnInput
  stake?: string
}

export const usePlaySlip = (productName?: string) => {
  const licensedTerritory = useLicensedTerritory({ raw: true })
  const route = useRoute()
  const { navigateToLocalized, removeQueryParams } = useRouterUtils()
  const visiblePlayId = useState('visiblePlayId', () => 'play-1')
  const { addToast } = useToaster()
  const { t } = useNuxtApp().$i18n

  const productId = productName || String(route.params.lotteryId || route.query?.p)
  const lotteryId = getLotteryIdInCorrectCase(productId)

  if (!lotteryId) throw new Error('usePlaySlip composable can only be used in valid lottery routes')

  const {
    addPlay,
    deleteLotterySelection,
    deletePrepaidSubscriptionAmount,
    playsByLottery,
    prepaidSubscriptionAmountByLottery,
    removePlay,
    selectedAddOnsByLottery,
    setPlays,
    setPrepaidSubscriptionAmount,
    setSelectedAddOns,
    setTicketType,
    ticketTypeByLottery,
    updatePlay,
  } = useLotteryStore()

  const { getProductFromRoute } = useProductGetter()

  const queryVariables = {
    licensedTerritory,
    lotteryId,
  }
  const queryResult = useLotteryQuery(queryVariables)

  queryResult.onError(async (error) => {
    if (
      !['lottery.notfound', 'error.invalid.input'].includes(error.message as BackendLotteryError)
    ) {
      return
    }
    if (import.meta.client) await navigateToLocalized('/')
  })

  const { isNumberLottery, isPickLottery } = useLotteryType(lotteryId)

  const { getByPrefix } = useQueryParams()

  const addOns = computed(() => config.value?.addOns ?? [])

  const addOnRequirements = computed(() => getAddOnRequirements(addOns.value.filter(isNotPerPlay)))

  const afterPurchaseAddOns = computed(() => config.value?.afterPurchaseAddOns ?? [])

  const completePlays = computed(() => plays.value.filter((play) => play.isComplete))

  const config = computed<Maybe<LotteryConfig>>(
    () => queryResult.result.value?.lottery.currentConfig ?? null,
  )

  const currentPrice = computed(() => {
    if (!config.value || !priceList.value) return null
    const { numberOfDraws, stakes, version } = config.value

    const preparedAddOns = addOns.value
      .map((addOn) => {
        const value =
          selectedAddOns.value.find((selectedAddOn) => selectedAddOn.name === addOn.name)?.value ??
          ''

        return {
          ...addOn,
          value,
        }
      })
      .filter((addon) => addon.value)
    try {
      const filteredPlays = plays.value
        .filter((play) => play.isDirty && play.isValid)
        .map((play) => {
          const playIndex = plays.value.findIndex((_play) => play.id === _play.id)

          const addOns = [plays.value[playIndex].perPlayAddOn].filter(
            (v): v is LotterySelectedPerPlayAddOn => !!v?.value,
          )

          const fields = play.fields.filter((field) => {
            const ignoredForAddOns = config.value?.numbers.find(
              ({ name }) => name === field.name,
            )?.ignoredForAddOns

            return !ignoredForAddOns?.some(
              (ignoredForAddOn) => ignoredForAddOn.value === addOns[0]?.value,
            )
          })

          return {
            addOns,
            fields,
            stake: {
              price: play.stake ?? stakes[0].price,
            },
          }
        })

      if (filteredPlays.length === 0) return null

      return calculatePlaySlipPrices(
        {
          addOns: preparedAddOns,
          configVersion: version,
          lotteryId,
          numberOfDraws: numberOfDraws[0] * (prepaidSubscriptionAmount.value?.value || 1),
          plays: filteredPlays,
        },
        {
          ...priceList.value,
          currency: 'USD',
        },
      )
    } catch {
      return null
    }
  })

  const extraHit = computed(() => perPlayAddOn.value?.name === 'extrahit')

  const fieldConfig = computed(() => config.value?.numbers ?? [])

  const firstIncompleteAndDirtyPlayId = computed(
    () => incompleteAndDirtyPlays.value[0]?.id ?? 'play-1',
  )

  const firstIncompletePlayId = computed(
    () =>
      plays.value.find(({ isComplete }) => !isComplete)?.id ?? plays.value.at(-1)?.id ?? 'play-1',
  )

  const hasDirtyPlays = computed(() => plays.value.some(({ isDirty }) => isDirty))

  const hasEmptyPlays = computed(() =>
    plays.value.some((play) => !play.isComplete && !play.isDirty),
  )

  const hasIncompletePlays = computed(() => !!incompleteAndDirtyPlays.value.length)

  const hasNoCompletePlays = computed(() => !plays.value.some((play) => play.isComplete))

  const isAllowed = computed(
    () => !!config.value?.allowedPlays?.includes(completePlays.value.length),
  )

  const incompleteAndDirtyPlays = computed(
    () => plays.value.filter((play) => !play.isComplete && play.isDirty) ?? [],
  )

  const isFixedStake = computed(() => stakes.value.length <= 1)

  const isSubscriptionAllowed = computed(() => !!config.value?.subscriptionsAllowed)

  const initialStake = computed(() => {
    if (isNumberLottery.value || !stakes.value.length) return ''
    return stakes.value[0].price
  })

  const prepaidSubscription = computed(() => config.value?.prepaidSubscription)

  const isPrepaidSubscriptionAllowed = computed(() => !!config.value?.prepaidSubscription)

  const subscriptionOptions = computed(() => {
    const isSubscription = ticketType.value === LotteryTicketType.Subscription
    const isPrepaidSubscription = ticketType.value === LotteryTicketType.PrepaidSubscription

    if (!isSubscription && !isPrepaidSubscription) return

    const type = isPrepaidSubscription ? SubscriptionType.Prepaid : SubscriptionType.Renewing

    const drawsPerPurchase =
      isPrepaidSubscription && prepaidSubscriptionAmount.value
        ? prepaidSubscriptionAmount.value.value
        : 1

    return {
      drawsPerPurchase,
      type,
    }
  })

  const lastEditedPlayId = computed(
    () => plays.value.filter(({ isDirty }) => isDirty).at(-1)?.id ?? 'play-1',
  )

  const lotteryType = computed(() => config.value?.type ?? null)

  const maxAllowedPlays = computed(() => config.value?.allowedPlays.at(-1) ?? 1)

  const nextIncompletePlayId = computed(() => {
    const visiblePlayIndex = getPlayIndex(visiblePlayId.value)
    const playsAfter = plays.value.slice(visiblePlayIndex + 1)
    const playsBefore = plays.value.slice(0, visiblePlayIndex)
    const surroundingIncompletePlays = [...playsAfter, ...playsBefore].filter(
      ({ isComplete }) => !isComplete,
    )

    return surroundingIncompletePlays[0]?.id
  })

  const perPlayAddOn = computed(() => addOns.value.find(isPerPlay))

  const plays = computed(() => {
    if (!config.value) return []
    return playsByLottery(lotteryId, config.value)
  })

  const prepaidSubscriptionAmount = computed(() => prepaidSubscriptionAmountByLottery(lotteryId))

  const prepaidSubscriptionCustomDrawsMin = computed(
    () => prepaidSubscription.value?.customDrawsMin ?? 4,
  )

  const prepaidSubscriptionCustomDrawsMax = computed(
    () => prepaidSubscription.value?.customDrawsMax ?? 100,
  )

  const priceList = computed(() => queryResult.result.value?.lottery.priceList ?? null)

  const properties = computed(() => queryResult.result.value?.lottery.properties ?? [])

  const quickPickConfig = computed(() => config.value?.quickPick ?? null)

  const selectedAddOns = computed(() => {
    if (!config.value) return []
    return selectedAddOnsByLottery(lotteryId, config.value)
  })

  const selectedPerPlayAddOn = computed(() => {
    const playIndex = getPlayIndex(visiblePlayId.value)
    return plays.value[playIndex]?.perPlayAddOn ?? null
  })

  const stakes = computed(() => config.value?.stakes ?? [])

  const ticketType = computed(() => ticketTypeByLottery(lotteryId))

  const addEmptyPlay = () => {
    if (!config.value || plays.value.length >= maxAllowedPlays.value) return
    addPlay(
      lotteryId,
      updateNumberLotteryPlay(config.value, plays.value.length, {
        perPlayAddOn: getInitialPerPlayAddOns(config.value)[0],
      }),
    )
  }

  const applyAddOnsFromQuery = async () => {
    const addOnParams = getByPrefix(QUERY_ADDON_PREFIX).map(({ name, value }) => ({
      name,
      value: value === 'true' ? 'true' : '',
    }))
    for (const addOnParam of addOnParams) {
      if (
        addOns.value
          .filter(isNotPerPlay)
          .find((addOn) => addOn.name.toLowerCase() === addOnParam.name.toLowerCase())
      ) {
        setSelectedAddOn(addOnParam.name, addOnParam.value)
      }
    }

    const addOnParamsKeys = addOnParams.map(
      (addOnParam) => `${QUERY_ADDON_PREFIX}_${addOnParam.name}`,
    )
    await removeQueryParams(...addOnParamsKeys)
  }

  const applyTicketTypeFromQuery = async () => {
    const subscription = route.query[QUERY_SUBSCRIPTION]
    if (!subscription) return

    const subscriptionValue = Number(subscription)
    const isNumber = !isNaN(subscriptionValue) && subscriptionValue > 0
    const isSubscription = subscription === 'true' || isNumber

    let ticketType = LotteryTicketType.Single

    if (isSubscriptionAllowed.value && isSubscription) ticketType = LotteryTicketType.Subscription

    if (isPrepaidSubscriptionAllowed.value && !isSubscription) {
      setPrepaidSubscriptionAmount(lotteryId, null)
    }

    if (isPrepaidSubscriptionAllowed.value && isSubscription) {
      ticketType = LotteryTicketType.PrepaidSubscription

      const selectableOptions = prepaidSubscription.value?.selectableDrawsOptions ?? []

      let label = `${selectableOptions[0]}`
      let value = selectableOptions[0]

      if (isNumber) {
        const isSelectableOption = selectableOptions.includes(subscriptionValue)
        label = isSelectableOption ? `${subscriptionValue}` : 'custom'
        value = Math.min(
          Math.max(subscriptionValue, prepaidSubscriptionCustomDrawsMin.value),
          prepaidSubscriptionCustomDrawsMax.value,
        )
      }

      setPrepaidSubscriptionAmount(lotteryId, {
        label,
        value,
      })
    }

    setTicketType(lotteryId, ticketType)
    await removeQueryParams(QUERY_SUBSCRIPTION)
  }

  const clearPlay = (playId: string) => {
    setSelectedNumbersForPlay(
      playId,
      fieldConfig.value.map<number[]>(() => []),
    )
  }

  const deletePlay = (playId: string) => {
    removePlayByIndex(getPlayIndex(playId))

    if (!config.value || !isPickLottery.value) return
  }

  const deleteAllPlays = () => {
    setPlays(lotteryId, [])
    addEmptyPlay()
  }

  const fillPlaySlipWithQuickPick = (quantity?: Maybe<number>, options?: QuickPickOptions) => {
    reset()

    const updatedAddOns = selectedAddOns.value.map((addOn) => {
      const quickPickAddOn = quickPickConfig.value?.addOns.find(({ name }) => name === addOn.name)
      return {
        ...addOn,
        value: quickPickAddOn ? quickPickAddOn.value : addOn.value,
      }
    })

    setSelectedAddOns(lotteryId, sanitizeSelectedAddOns(addOnRequirements.value, updatedAddOns))

    const maxPlays = config.value?.allowedPlays.length || quickPickConfig.value?.plays || 0

    const cappedPlayAmount =
      quantity && Number.isInteger(quantity) ? Math.min(quantity, maxPlays) : undefined

    const playsWithQuickPickAmount = cappedPlayAmount ?? quickPickConfig.value?.plays ?? 0

    for (let i = 0; i < playsWithQuickPickAmount; i++) {
      const playId = `play-${i + 1}`
      if (!hasEmptyPlays.value) addEmptyPlay()
      fillPlaysWithQuickPick([playId], options)
    }

    return playsWithQuickPickAmount
  }

  const fillPlaysWithQuickPick = (playIds: string[], options?: QuickPickOptions) => {
    plays.value.forEach((play) => {
      if (!playIds.includes(play.id)) {
        return {
          isQuickPick: play.isQuickPick,
          numbers: play.fields.map((field) => field.selected),
        }
      }

      const isDuplicate = ref(false)
      const isValidPick = ref(true)

      let updatedPlay: QuickPickPlay

      do {
        updatedPlay = {
          isQuickPick: true,
          numbers: getNumbersForQuickPick(play, options),
        }

        if (isPickLottery.value) {
          updatedPlay.perPlayAddOn = getBetTypeForQuickPick(options)
          updatedPlay.stake = getStakeForQuickPick(options)
        }

        if (options?.validateFn) {
          isValidPick.value = options.validateFn(updatedPlay.numbers)
        } else if (isPickLottery.value) {
          isValidPick.value = validatePlay(
            updatedPlay.perPlayAddOn?.value,
            updatedPlay.numbers.flatMap((numbers) => numbers),
          )
        }

        if (isPickLottery.value) continue
        isDuplicate.value = plays.value.some((play) => {
          if (!play.isComplete) return false
          const fields = play.fields.map(({ selected }) => selected)

          return isDeepEqual(fields, updatedPlay.numbers)
        })
      } while (isDuplicate.value || !isValidPick.value)

      const fields = convertSelectedNumbersToFields(updatedPlay.numbers)
      updatePlay(lotteryId, config.value, play.id, {
        ...updatedPlay,
        fields,
        isValid: isPickLottery.value ? true : undefined,
      })
    })
  }

  const getPlayIndex = (playId: string) => plays.value.findIndex(({ id }) => id === playId)

  const isExtraHitEnabled = (playId: string) => {
    if (!extraHit.value) return false

    const playIndex = getPlayIndex(playId)
    const perPlayAddOn = plays.value[playIndex]?.perPlayAddOn
    return !!perPlayAddOn?.value
  }

  const getBetTypeForQuickPick = (options?: QuickPickOptions) => {
    if (!perPlayAddOn.value) return
    const betType = perPlayAddOn.value?.options.find(
      (option) => normalizeKey(option) === normalizeKey(String(options?.betType)),
    )
    const value = betType ?? getRandomValueFromArray(perPlayAddOn.value.options)
    if (!value) return
    return {
      name: perPlayAddOn.value?.name,
      value,
    }
  }

  const getNumbersForQuickPick = (play: PlaySlipPlay, options?: QuickPickOptions) => {
    const preparedPlay = getPreparedPlay(play, options)
    return fieldConfig.value.map((field, fieldIndex) =>
      field.name === 'EXTRA' && isExtraHitEnabled(play.id)
        ? play.fields[fieldIndex].selected
        : quickPickPlay(preparedPlay, field, fieldIndex, !options?.numbers),
    )
  }

  const getPreparedPlay = (play: PlaySlipPlay, options?: QuickPickOptions): PlaySlipPlay => {
    const preselectedNumbers = getPreselectedNumbers(options?.numbers)
    const isValidPlay = validatePlay(
      options?.betType,
      preselectedNumbers?.flatMap((numbers) => numbers),
    )

    if (isValidPlay && preselectedNumbers) {
      return {
        ...play,
        fields: preselectedNumbers.map((numbers, index) => ({
          ...play.fields[index],
          selected: numbers.filter((value) => typeof value === 'number'),
        })),
      }
    }

    return {
      ...play,
      fields: play.fields,
    }
  }

  const getPreselectedNumbers = (numbers: QuickPickOptions['numbers']) => {
    if (!numbers?.length) return
    return fieldConfig.value.map(({ max, min, selectableMax }, fieldIndex) =>
      Array.from({ length: selectableMax }, (_, index) => {
        const value = numbers[fieldIndex + index]
        if (typeof value !== 'number' || value < min || value > max) return
        return value
      }),
    )
  }

  const getStakeForQuickPick = (options?: QuickPickOptions) => {
    if (options?.stake) return options.stake

    const stake = priceList.value?.charges
      .filter(isFixedCharge)
      .find(
        ({ id, value }) =>
          value.value === options?.wager && stakes.value.some((stake) => id === stake.price),
      )
    return stake?.id ?? getRandomValueFromArray(stakes.value)?.price
  }

  const reset = () => {
    deleteLotterySelection(lotteryId)
  }

  const setSelectedAddOn = (
    name: LotterySelectedAddOn['name'],
    value: LotterySelectedAddOn['value'],
  ) => {
    let updatedAddOns = selectedAddOns.value.map((addOn) => ({ ...addOn }))
    const addOnIndex = updatedAddOns.findIndex(
      (addOn) => addOn.name.toLowerCase() === name.toLowerCase(),
    )

    if (addOnIndex === -1) {
      updatedAddOns = addOns.value.filter(isNotPerPlay).map<LotterySelectedAddOn>((addOn) => {
        const selectedAddOn = selectedAddOns.value?.find(({ name }) => name === addOn.name)
        return {
          name: addOn.name,
          value: selectedAddOn ? selectedAddOn.value : '',
        }
      })
    }

    updatedAddOns[addOnIndex].value = value
    setSelectedAddOns(lotteryId, sanitizeSelectedAddOns(addOnRequirements.value, updatedAddOns))
  }

  const setSelectedPerPlayAddOn = (playId: string, updatedAddOn: LotterySelectedPerPlayAddOn) => {
    const playIndex = getPlayIndex(playId)
    if (playIndex < 0) return

    updatePlay(lotteryId, config.value, playId, {
      perPlayAddOn: updatedAddOn,
    })
  }

  const setSelectedStake = (playId: string, updatedStake: string) => {
    const playIndex = getPlayIndex(playId)
    if (playIndex < 0) return

    updatePlay(lotteryId, config.value, playId, {
      stake: updatedStake,
    })
  }

  const setValidPlay = (playId: string, isValid: boolean) => {
    const playIndex = getPlayIndex(playId)
    if (playIndex < 0) return

    updatePlay(lotteryId, config.value, playId, {
      isValid,
    })
  }

  const setSelectedNumbersForField = (playId: string, fieldIndex: number, value: number[]) => {
    const playIndex = getPlayIndex(playId)
    if (playIndex < 0 || !config.value) return

    const fields = plays.value[playIndex]?.fields.map((field, index) => ({
      ...field,
      selected: index === fieldIndex ? value : field?.selected,
    }))

    updatePlay(lotteryId, config.value, playId, {
      fields,
    })
  }

  const convertSelectedNumbersToFields = (selectedNumbers: number[][]): FieldInput[] =>
    fieldConfig.value.map((field, index) => {
      const selected = selectedNumbers[index] || []
      return {
        name: field.name,
        selected,
      }
    })

  const setSelectedNumbersForPlay = (playId: string, value: number[][]) => {
    const playIndex = getPlayIndex(playId)
    if (playIndex < 0 || !config.value) return

    updatePlay(lotteryId, config.value, playId, {
      fields: convertSelectedNumbersToFields(value),
    })
  }

  const updateAllPlays = () => {
    plays.value.forEach((play) => {
      updatePlay(lotteryId, config.value, play.id, {
        isQuickPick: play.isQuickPick,
      })
    })
  }

  const getAddOnRequirement = (name: string) => getRequiredAddOn(addOnRequirements.value, name)

  const isAddOnDisabled = (name: string) =>
    isAddOnRestricted(addOnRequirements.value, selectedAddOns.value, name)

  const isAddOnSelected = (name: string) => hasSelectedAddOn(selectedAddOns.value, name)

  const removePlayByIndex = (playIndex: number) => {
    removePlay(lotteryId, playIndex)
    updateAllPlays()
  }

  const storePlaySlip = async () => {
    if (!config.value) return

    const { numberOfDraws, stakes, version } = config.value
    const { couponCode: bonusCode } = useHypeStore()
    const { mutate } = useStorePlaySlipMutation()

    try {
      const result = await mutate({
        input: {
          addOns: selectedAddOns.value.filter((addOn) => addOn.value),
          bonusCode,
          configVersion: version,
          licensedTerritory,
          lotteryId,
          numberOfDraws: numberOfDraws[0],
          plays: completePlays.value.map((play) => {
            const playIndex = plays.value.findIndex((_play) => play.id === _play.id)

            const selectedPerPlayAddOn = plays.value[playIndex].perPlayAddOn

            return {
              addOns: selectedPerPlayAddOn?.value ? [selectedPerPlayAddOn] : [],
              fields: play.fields
                .filter((field) => !!field)
                .map((field) => {
                  const isIgnored = config.value?.numbers
                    .find((number) => number.name === field.name)
                    ?.ignoredForAddOns.some((addOn) => addOn.value === selectedPerPlayAddOn?.value)

                  return {
                    name: field.name,
                    selected: isIgnored ? [] : field.selected,
                  }
                }),
              stake: {
                price: play.stake ?? stakes[0].price,
              },
            }
          }),
          subscriptionOptions: subscriptionOptions.value,
        },
      })
      if (!result?.data) return

      const product = productName ?? getProductFromRoute(route)
      if (!product) return

      // TODO restriction handling - MIRA-7718 has to be done before
      useProductStore().setProduct(result.data.storePlaySlip.id, product, ProductType.Lottery)
    } catch (error) {
      if (
        isSpecificGraphQLError(error, {
          matchMessage: 'validation.subscription.notallowed',
        })
      ) {
        deletePrepaidSubscriptionAmount(lotteryId)
        setTicketType(lotteryId, LotteryTicketType.Single)
        addToast({
          autoClose: false,
          headline: t('toast.prepaidsubscription.notallowed.headline'),
          message: t('toast.prepaidsubscription.notallowed.message'),
          tracking: { topicKey: 'prepaid-subscription-not-allowed' },
          type: 'info',
        })
      }

      throw error
    }
  }

  const validatePlay = (betType?: Maybe<string>, numbers?: Maybe<Maybe<number | undefined>[]>) => {
    if (!(lotteryId in LOTTERY_ADD_ONS)) return true

    const preparedNumbers: number[][] = (numbers ?? [])
      .filter((n): n is number => n !== null && n !== undefined)
      .map((num) => [num])

    const fields = convertSelectedNumbersToFields(preparedNumbers).filter(
      (field) => field.selected.length > 0,
    )

    const onlyNecessaryNumbers = fields
      .flatMap(({ name, selected }) => {
        const ignoredForAddOns = config.value?.numbers.find(
          ({ name: fieldName }) => fieldName === name,
        )?.ignoredForAddOns

        const isIgnored = ignoredForAddOns?.some(
          (ignoredForAddOn) => ignoredForAddOn.value === betType,
        )

        return isIgnored ? null : selected
      })
      .filter((n): n is number => n !== null)

    const disallowedPlays = LOTTERY_ADD_ONS[lotteryId as unknown as LotteryEnumsId].betTypes?.find(
      ({ name }) => name === betType,
    )?.disallowedPlays
    if (!disallowedPlays) return true

    return !disallowedPlays.some((disallowed) => isDeepEqual(onlyNecessaryNumbers, disallowed))
  }

  return {
    addEmptyPlay,
    addOns,
    afterPurchaseAddOns,
    applyAddOnsFromQuery,
    applyTicketTypeFromQuery,
    clearPlay,
    completePlays,
    config,
    currentPrice,
    deleteAllPlays,
    deletePlay,
    extraHit,
    fieldConfig,
    fillPlaySlipWithQuickPick,
    fillPlaysWithQuickPick,
    firstIncompleteAndDirtyPlayId,
    firstIncompletePlayId,
    getAddOnRequirement,
    getPlayIndex,
    hasDirtyPlays,
    hasEmptyPlays,
    hasIncompletePlays,
    hasNoCompletePlays,
    incompleteAndDirtyPlays,
    initialStake,
    isAddOnDisabled,
    isAddOnSelected,
    isAllowed,
    isExtraHitEnabled,
    isFixedStake,
    isLoading: queryResult.loading,
    isPrepaidSubscriptionAllowed,
    isSubscriptionAllowed,
    lastEditedPlayId,
    licensedTerritory,
    lotteryId,
    lotteryType,
    maxAllowedPlays,
    nextIncompletePlayId,
    perPlayAddOn,
    plays,
    prepaidSubscription,
    prepaidSubscriptionAmount,
    prepaidSubscriptionCustomDrawsMax,
    prepaidSubscriptionCustomDrawsMin,
    priceList,
    properties,
    removePlayByIndex,
    reset,
    selectedAddOns,
    selectedPerPlayAddOn,
    setPrepaidSubscriptionAmount,
    setSelectedAddOn,
    setSelectedAddOns,
    setSelectedNumbersForField,
    setSelectedNumbersForPlay,
    setSelectedPerPlayAddOn,
    setSelectedStake,
    setTicketType,
    setValidPlay,
    stakes,
    storePlaySlip,
    subscriptionOptions,
    ticketType,
    updateAllPlays,
    validatePlay,
    visiblePlayId,
  }
}
